import { MdAdd } from '@teamworksdev/icons';

import { Button } from '@mui/material';

import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { MdViewColumn } from '@teamworksdev/icons';
import { RefObject, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DisabledWithTooltip } from '../../../../components/permissions/DisabledWithTooltip';
import { useViewer } from '../../../../context/viewer';
import { usePlanContext } from '../../context/PlanContext';
import { getLastTrackRowIndex, TimelineRow } from '../track-rows';
import { DateScale, TrackForEdit, TrackPreview } from '../types';
import { DATE_HEADER_HEIGHT, SIDE_PANEL_WIDTH, stylesForIndex } from '../utils';
import { AdditionalTeamsTracks } from './AdditionalTeamsTracks';
import { AnnualPlannerUnavailableState } from './AnnualPlannerUnavailableState';
import { ArchivedTracksSection } from './ArchivedTracksSection';
import { Tracks } from './Tracks';

type TrackPanelProps = {
  tracks: TrackPreview[];
  rows: TimelineRow<TrackPreview>[];
  dateScale: DateScale;
  setDateScale: (dateScale: DateScale) => void;
  openAddTrackDrawer: (open: boolean, parentTrack?: TrackPreview) => void;
  setTrackForEdit: (forEdit: TrackForEdit) => void;
  onReorderTracks: (tracks: TrackPreview[]) => void;
  resetScroll: () => void;
  trackPanelRef: RefObject<HTMLDivElement>;
  handleScrollFirst: React.UIEventHandler<HTMLDivElement>;
  collapsedTracks: string[];
  toggleTrackCollapse: (trackId: string) => void;
  refetchTracks: () => void;
  openViewArchivedTrackDrawer: (trackId: string) => void;
  openMoveSubTrack: (
    subTrack: TrackPreview,
    destinationTrack?: TrackPreview,
  ) => void;
  subTrackReorganizationFlag: boolean;
};

export function TrackPanel({
  dateScale,
  setDateScale,
  tracks,
  rows,
  openAddTrackDrawer,
  setTrackForEdit,
  onReorderTracks,
  resetScroll,
  trackPanelRef,
  handleScrollFirst,
  collapsedTracks,
  toggleTrackCollapse,
  refetchTracks,
  openViewArchivedTrackDrawer,
  openMoveSubTrack,
  subTrackReorganizationFlag,
}: TrackPanelProps) {
  const { t } = useTranslation();
  const { hasPermissions } = useViewer();
  // Mirrors BlockPanel: the Phases header row (and the timeline phase row
  // it aligns with) is removed when the viewer can't see phases.
  const canViewPhases = hasPermissions('plan.phases', 'view');
  // The rows' own actions are gated one by one (see TrackSidePanelItem); the
  // drag is a single gesture over all of them, so it takes the permission that
  // every drop's write needs.
  const canEditTracks = hasPermissions('plan.tracks', 'edit');
  const { archivedSectionExpanded, toggleArchivedSection } = usePlanContext();

  const archivedHeaderRow = useMemo(
    () => rows.find((row) => row.kind === 'archived-header'),
    [rows],
  );
  // Index of the row that owns the "end of section" border-b — see
  // getLastTrackRowIndex for why this must be the row genuinely last
  // overall, not merely the last track-kind row.
  const lastTrackIndex = getLastTrackRowIndex(rows);

  return (
    <div
      style={{
        width: SIDE_PANEL_WIDTH,
      }}
      className="h-full relative border-r flex-shrink-0 flex flex-col overflow-y-auto"
    >
      <div
        className="border-b flex items-center justify-around sticky flex-shrink-0"
        style={{
          top: 0,
          height: DATE_HEADER_HEIGHT,
        }}
      >
        <ToggleButtonGroup
          value={dateScale}
          size="small"
          exclusive
          onChange={(_, value) => {
            if (value === null) return;
            // The value can only be month, quarter, or year so this is a safe cast
            setDateScale(value as DateScale);
          }}
        >
          <ToggleButton value="month">{t('month')}</ToggleButton>
          <ToggleButton value="quarter">{t('quarter')}</ToggleButton>
          <ToggleButton value="year">{t('year')}</ToggleButton>
        </ToggleButtonGroup>
        <Button
          variant="outlined"
          onClick={() => {
            resetScroll();
          }}
        >
          {t('today')}
        </Button>
      </div>
      {canViewPhases && (
        <div
          className="bg-gray-50 flex flex-row w-full py-3 pr-3 pl-6 items-center border-b sticky"
          style={stylesForIndex(0)}
        >
          <MdViewColumn
            aria-hidden="true"
            size={18}
            className="mr-2 text-gray-500"
          />
          <p className="text-left flex-grow w-full font-semibold text-sm truncate">
            {t('phases')}
          </p>
        </div>
      )}
      {tracks && hasPermissions('plan.tracks', 'view') ? (
        <Tracks
          onScroll={handleScrollFirst}
          scrollRef={trackPanelRef}
          tracks={tracks}
          lastTrackIndex={lastTrackIndex}
          collapsedTracks={collapsedTracks}
          toggleTrackCollapse={toggleTrackCollapse}
          setTrackForEdit={setTrackForEdit}
          onReorderTracks={onReorderTracks}
          openAddTrackDrawer={openAddTrackDrawer}
          refetchTracks={refetchTracks}
          openMoveSubTrack={openMoveSubTrack}
          subTrackReorganizationFlag={subTrackReorganizationFlag}
          canEditTracks={canEditTracks}
        >
          {archivedHeaderRow && (
            <ArchivedTracksSection
              rows={rows}
              setTrackForEdit={setTrackForEdit}
              openAddTrackDrawer={openAddTrackDrawer}
              refetchTracks={refetchTracks}
              collapsedTracks={collapsedTracks}
              toggleTrackCollapse={toggleTrackCollapse}
              expanded={archivedSectionExpanded}
              onToggleSection={toggleArchivedSection}
              headerIndex={archivedHeaderRow.index}
              archivedGroupCount={archivedHeaderRow.archivedGroupCount}
              lastTrackIndex={lastTrackIndex}
              openViewArchivedTrackDrawer={openViewArchivedTrackDrawer}
            />
          )}
          <AdditionalTeamsTracks />
        </Tracks>
      ) : (
        // Tracks View revoked suppresses the track rows server-side, so the
        // list would render empty — the Figma empty state replaces it (and
        // the Add Track affordance) instead.
        <AnnualPlannerUnavailableState
          stateKey="unableToDisplayTracks"
          variant="inline"
        />
      )}

      {hasPermissions('plan.tracks', 'create') ? (
        <div className="absolute bottom-4 left-4 bg-white">
          <Button variant="outlined" onClick={() => openAddTrackDrawer(true)}>
            <MdAdd
              aria-hidden="true"
              size={18}
              className="mr-2 text-gray-500"
            />
            {t('addTrack')}
          </Button>
        </div>
      ) : (
        <div className="absolute bottom-4 left-4 bg-white">
          <DisabledWithTooltip
            disabled
            reason={t('permissionDisabled.create', { item: t('tracks') })}
          >
            <Button variant="outlined">
              <MdAdd
                aria-hidden="true"
                size={18}
                className="mr-2 text-gray-500"
              />
              {t('addTrack')}
            </Button>
          </DisabledWithTooltip>
        </div>
      )}
    </div>
  );
}
